import { useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import Dropdown from '../form-inputs/inputDropdown';
import PostViewAs from '../../services/adapters/viewAs';
import PostViewAsOrganisation from '../../services/adapters/viewAsOrganisation';
import Button from '../buttons/baseButton';
import DownloadButton from '../buttons/downloadButton';
import type { OptionType } from '../../types/forms.type';
import GetRolesAndOrganisations from '../../services/adapters/rolesAndOrganisations';
import PostClientRenewalsPack from '../../services/adapters/clientRenewalsPack';
import useAuth from '../../utils/auth';
import Role from '../../types/enums';

function SuperAdminPanel() {
  const [loading, setLoading] = useState(false);
  const [displayText, setDisplayText] = useState('Change Organisation');
  const [dropdownStates, setdropdownStates] = useState({ superAdminOrgDropdown: '', superAdminRoleDropdown: '' });
  const [orgsAndRoles, setOrgsAndRoles] = useState<{ roles: OptionType[]; organisations: OptionType[] }>({ roles: [], organisations: [] });
  const authUser = useAuth();
  const isSuperAdmin = authUser.role === Role.SuperAdmin;

  const handleChange = (dropdownId: string, value: SingleValue<OptionType>) => {
    const selectedValue = value ? value.value : '';
    setdropdownStates((previous) => ({ ...previous, [dropdownId]: selectedValue }));
  };

  const changeOrganisation = async (organisation: string) => {
    try {
      setLoading(true);
      if (isSuperAdmin) {
        const viewAsInstance = new PostViewAs();
        await viewAsInstance.adaptResponse({ role: dropdownStates.superAdminRoleDropdown, organisation });
      } else {
        const viewAsInstance = new PostViewAsOrganisation();
        await viewAsInstance.adaptResponse({ organisation });
      }
      window.location.href = '/';
    } catch (error) {
      if (error instanceof Error) {
        throw new Error('Failed to change organisation');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetOrganisation = async () => {
    try {
      setLoading(true);
      if (isSuperAdmin) {
        const viewAsInstance = new PostViewAs();
        await viewAsInstance.adaptResponse({ role: '', organisation: '' });
        setDisplayText('Change Roles/Organisations');
      } else {
        const viewAsInstance = new PostViewAsOrganisation();
        await viewAsInstance.adaptResponse({ organisation: '' });
        setDisplayText('Change Organisation');
      }
      window.location.href = '/';
    } catch (error) {
      if (error instanceof Error) {
        throw new Error('Failed to reset organisation');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getRolesAndOrganisations = async () => {
      try {
        const adapter = new GetRolesAndOrganisations();
        const response = await adapter.adaptResponse();
        const roleOptions = response.roles.map((role) => ({ label: role, value: role }));
        const orgOptions = response.organisations.map((org) => ({ label: org, value: org }));
        setOrgsAndRoles({ roles: roleOptions, organisations: orgOptions });
      } catch (error) {
        if (error instanceof Error) {
          throw new Error('Failed to fetch roles and organisations');
        }
      }
    };
    getRolesAndOrganisations();
  }, []);

  return (
    <div className="border border-pipgrey-light rounded-md p-2">
      <div className="flex flex-col md:flex-row justify-between ">
        <div className="mt-2 space-y-2 ps-2 flex flex-col md:w-1/2 h-1/2 flex-1">
          <Dropdown
            id="superAdminOrgDropdown"
            label="Organisations"
            placement="top"
            onChange={handleChange}
            selected={dropdownStates.superAdminOrgDropdown}
            options={orgsAndRoles.organisations}
          />
          {isSuperAdmin && (
            <Dropdown
              id="superAdminRoleDropdown"
              label="Roles"
              placement="top"
              onChange={handleChange}
              selected={dropdownStates.superAdminRoleDropdown}
              options={orgsAndRoles.roles}
            />
          )}
        </div>
        <div id="superAdminPanel-button-container" className="m-2 p-2 md:mt-auto flex flex-row md:flex-col md:w-1/2  gap-2 h-1/2 md:items-end items-center">
          <Button
            id="reset-role-organisation-button"
            className="button-default"
            label="Reset"
            onClick={resetOrganisation}
          />
          <Button
            id="change-role-organisation-button"
            className="button-green"
            label={loading ? 'Updating...' : displayText}
            onClick={() => changeOrganisation(dropdownStates.superAdminOrgDropdown)}
          />
          <DownloadButton
            id="generate-client-renewals-pack-button"
            className="button-default"
            label="Generate Client Renewals Pack"
            fetchUrl={() => new PostClientRenewalsPack().adaptResponse({
              liftClientDisplayName: dropdownStates.superAdminOrgDropdown,
            })}
          />

        </div>

      </div>
    </div>

  );
}

export default SuperAdminPanel;
